import { useState, useEffect, useRef } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getCurrentUser } from '../../services/auth';
import { deductCredits } from '../../services/credits';
import { modelAPI } from '../../services/api';
import { createHistory, getUserHistory, updateHistory } from '../../services/history';
import { isArkVideoGenerationModel } from '../../services/video';

interface StoryboardImage {
  id: number;
  url: string;
  prompt: string;
}

export default function OneClickVideoPage() {
  const [activeMenu, setActiveMenu] = useState('一键出大片');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [productName, setProductName] = useState('');
  const [productFeatures, setProductFeatures] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('真实');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isGeneratingStoryboard, setIsGeneratingStoryboard] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [storyboardImages, setStoryboardImages] = useState<StoryboardImage[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedRatio, setSelectedRatio] = useState('9:16');
  const [selectedDuration, setSelectedDuration] = useState('5秒');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [volcengineConfig, setVolcengineConfig] = useState<any>(null);
  const [imageModelConfig, setImageModelConfig] = useState<any>(null);
  const [videoTaskId, setVideoTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [isCheckingTaskStatus, setIsCheckingTaskStatus] = useState(false);
  const [progress, setProgress] = useState(0);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);

  // 获取用户信息和火山模型配置
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);

        const allModels = await modelAPI.getAllModels();
        const activeModels = (allModels || []).filter((model: any) => model?.is_active);

        // 获取当前启用的视频/图像模型（供应商三选一后由 is_active 决定）
        const videoModels = activeModels
          .filter(
            (model: any) =>
              model.type === 'video' &&
              (model.provider !== 'doubao' || isArkVideoGenerationModel(model.model_name))
          )
          .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        if (videoModels && videoModels.length > 0) {
          setVolcengineConfig(videoModels[0]);
        }

        const imageModels = activeModels
          .filter((model: any) => model.type === 'image')
          .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        if (imageModels && imageModels.length > 0) {
          setImageModelConfig(imageModels[0]);
        }
      } catch (err) {
        console.error('获取用户信息失败:', err);
      }
    };

    fetchUserData();
  }, []);

  const styles = ['水墨', '线稿', '素描', '彩绘', '真实'];
  const ratios = [
    { label: '竖屏 9:16', value: '9:16' },
    { label: '横屏 16:9', value: '16:9' },
  ];
  const durations = ['5秒', '10秒'];

  const mockStoryboardImages: StoryboardImage[] = [
    {
      id: 1,
      url: 'https://readdy.ai/api/search-image?query=Professional%20product%20photography%20of%20modern%20smartphone%20on%20clean%20white%20background%2C%20elegant%20presentation%20with%20soft%20studio%20lighting%2C%20commercial%20style%2C%20high%20quality%20detail%20shot&width=640&height=360&seq=story1&orientation=landscape',
      prompt: '产品主图展示，简洁背景突出产品',
    },
    {
      id: 2,
      url: 'https://readdy.ai/api/search-image?query=Close-up%20hands%20holding%20and%20using%20smartphone%20with%20elegant%20gestures%2C%20lifestyle%20product%20photography%2C%20soft%20natural%20lighting%2C%20modern%20commercial%20style&width=640&height=360&seq=story2&orientation=landscape',
      prompt: '手持产品特写，展现使用场景',
    },
    {
      id: 3,
      url: 'https://readdy.ai/api/search-image?query=Smartphone%20screen%20interface%20display%20with%20dynamic%20visual%20effects%2C%20technology%20showcase%2C%20modern%20UI%20design%2C%20clean%20composition%2C%20commercial%20photography&width=640&height=360&seq=story3&orientation=landscape',
      prompt: '产品功能界面展示，科技感呈现',
    },
    {
      id: 4,
      url: 'https://readdy.ai/api/search-image?query=Product%20detail%20close-up%20showing%20premium%20texture%20and%20craftsmanship%2C%20luxury%20commercial%20photography%2C%20studio%20lighting%2C%20high-end%20quality%20presentation&width=640&height=360&seq=story4&orientation=landscape',
      prompt: '产品细节特写，质感展现',
    },
    {
      id: 5,
      url: 'https://readdy.ai/api/search-image?query=Lifestyle%20scene%20with%20product%20in%20modern%20environment%2C%20natural%20usage%20scenario%2C%20warm%20atmosphere%2C%20commercial%20photography%20style%2C%20professional%20quality&width=640&height=360&seq=story5&orientation=landscape',
      prompt: '生活场景应用，自然融入环境',
    },
    {
      id: 6,
      url: 'https://readdy.ai/api/search-image?query=Brand%20logo%20and%20product%20name%20display%20with%20clean%20background%2C%20professional%20commercial%20ending%20frame%2C%20elegant%20typography%2C%20premium%20quality%20presentation&width=640&height=360&seq=story6&orientation=landscape',
      prompt: '品牌标识展示，专业收尾画面',
    },
  ];

  const allowedImageTypes = ['image/png', 'image/jpeg', 'image/webp'];
  const maxImageSize = 5 * 1024 * 1024;

  const processImageFile = (file: File) => {
    if (!allowedImageTypes.includes(file.type)) {
      setError('仅支持 PNG / JPG / WEBP 格式的图片');
      return;
    }
    if (file.size > maxImageSize) {
      setError('图片大小不能超过 5MB');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onloadend = () => setSelectedImage(reader.result as string);
    reader.onerror = () => setError('读取图片失败，请稍后重试');
    reader.readAsDataURL(file);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processImageFile(file);
  };

  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processImageFile(file);
  };

  const handleImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleInitialize = () => {
    if (!selectedImage || !productName || !productFeatures) {
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmInitialize = async () => {
    if (!user) {
      setError('请先登录');
      return;
    }

    setShowConfirmModal(false);
    setIsInitializing(true);
    setError(null);

    try {
      // 扣除初始化积分
      const deductResult = await deductCredits(
        user.id,
        10,
        '初始化产品信息'
      );

      if (!deductResult.success) {
        setError(deductResult.error || '积分扣除失败');
        setIsInitializing(false);
        return;
      }

      // 生成分镜图
      const generatedStoryboard = await generateStoryboard();
      
      setIsInitializing(false);
      setIsInitialized(true);
      setStoryboardImages(generatedStoryboard);
    } catch (err: any) {
      setError(err.message || '初始化失败');
      setIsInitializing(false);
    }
  };

  const handleRegenerateStoryboard = async () => {
    if (!user) {
      setError('请先登录');
      return;
    }

    setIsGeneratingStoryboard(true);
    setError(null);

    try {
      // 扣除重生成积分
      const deductResult = await deductCredits(
        user.id,
        8,
        '重新生成分镜图'
      );

      if (!deductResult.success) {
        setError(deductResult.error || '积分扣除失败');
        setIsGeneratingStoryboard(false);
        return;
      }

      // 生成分镜图
      const generatedStoryboard = await generateStoryboard();
      
      setStoryboardImages(generatedStoryboard);
      setIsGeneratingStoryboard(false);
    } catch (err: any) {
      setError(err.message || '重生成失败');
      setIsGeneratingStoryboard(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!user) {
      setError('请先登录');
      return;
    }

    if (!volcengineConfig?.api_key) {
      setError('火山模型API密钥未配置，请联系管理员');
      return;
    }

    setIsGeneratingVideo(true);
    setError(null);

    try {
      const duration = selectedDuration.replace('秒', '');
      const creditsNeeded = selectedDuration === '10秒' ? 15 : 10;

      const deductResult = await deductCredits(
        user.id,
        creditsNeeded,
        `生成${selectedDuration}视频`
      );

      if (!deductResult.success) {
        setError(deductResult.error || '积分扣除失败');
        setIsGeneratingVideo(false);
        return;
      }

      const taskResult = await generateVideo(duration);
      
      setIsGeneratingVideo(false);
      if (taskResult && taskResult.id) {
        setVideoTaskId(taskResult.id);
        setShowSuccessModal(true);
      } else {
        setError('视频生成任务创建失败');
      }
    } catch (err: any) {
      setError(err.message || '视频生成失败');
      setIsGeneratingVideo(false);
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    // 开始轮询任务状态
    startPollingTaskStatus();
  };

  // 开始轮询任务状态
  const startPollingTaskStatus = () => {
    if (!videoTaskId) return;
    
    // 清除之前的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // 立即检查一次状态
    checkTaskStatus(videoTaskId);
    
    // 设置轮询，每3秒检查一次
    pollingIntervalRef.current = setInterval(async () => {
      if (videoTaskId) {
        await checkTaskStatus(videoTaskId);
      }
    }, 3000);
  };

  // 停止轮询任务状态
  const stopPollingTaskStatus = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // 组件卸载时清除轮询
  useEffect(() => {
    return () => {
      stopPollingTaskStatus();
    };
  }, []);

  const handleCheckTaskStatus = async () => {
    if (!videoTaskId) return;
    await checkTaskStatus(videoTaskId);
  };

  // 生成分镜图
  const generateStoryboard = async (): Promise<StoryboardImage[]> => {
    if (!imageModelConfig?.api_key) {
      console.warn('火山图像模型API密钥未配置，使用mock数据');
      return mockStoryboardImages;
    }

    try {
      setIsGeneratingStoryboard(true);
      const API_KEY = imageModelConfig.api_key;
      const model = imageModelConfig.model_name;
      const baseUrl = imageModelConfig.base_url || 'https://ark.cn-beijing.volces.com/api/v3';

      const prompt = `为产品"${productName}"生成${selectedStyle}风格的分镜图，产品特点：${productFeatures}。请生成9张不同角度和场景的产品展示图片，包括：1.产品主图展示，2.手持产品特写，3.产品功能界面展示，4.产品细节特写，5.生活场景应用，6.品牌标识展示，7.产品使用场景，8.产品包装展示，9.产品组合展示`;

      const imageRequest = {
        model: model,
        prompt: prompt,
        image: selectedImage || '',
        sequential_image_generation: 'auto',
        sequential_image_generation_options: {
          max_images: 6
        },
        response_format: 'url',
        size: '2K',
        stream: false,
        watermark: true
      };

      const response = await fetch(`${baseUrl}/images/generations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        },
        body: JSON.stringify(imageRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('图像生成失败:', errorData);
        throw new Error(errorData.error?.message || '图像生成请求失败');
      }

      const data = await response.json();
      console.log('图像生成成功:', data);

      const storyboardImages: StoryboardImage[] = [];

      if (data.data && Array.isArray(data.data)) {
        data.data.forEach((item: any, index: number) => {
          storyboardImages.push({
            id: index + 1,
            url: item.url || item.b64_json,
            prompt: `分镜${index + 1} - ${['产品主图展示，简洁背景突出产品', '手持产品特写，展现使用场景', '产品功能界面展示，科技感呈现', '产品细节特写，质感展现', '生活场景应用，自然融入环境', '品牌标识展示，专业收尾画面', '产品使用场景，展示产品功能', '产品包装展示，精美包装设计', '产品组合展示，多产品搭配'][index] || '产品展示'}`
          });
        });
      }

      setIsGeneratingStoryboard(false);
      return storyboardImages.length > 0 ? storyboardImages : mockStoryboardImages;
    } catch (err: any) {
      console.error('生成分镜图失败:', err);
      setError(err.message || '生成分镜图失败');
      setIsGeneratingStoryboard(false);
      return mockStoryboardImages;
    }
  };

  // 生成视频
  const generateVideo = async (duration: string) => {
    if (!volcengineConfig?.api_key) {
      throw new Error('火山模型API密钥未配置，请联系管理员');
    }

    if (!volcengineConfig?.model_name) {
      throw new Error('火山模型名称未配置，请联系管理员');
    }

    const ARK_API_KEY = volcengineConfig.api_key;
    const model = volcengineConfig.model_name;
    const baseUrl = volcengineConfig.base_url || 'https://ark.cn-beijing.volces.com/api/v3';
    
    const videoRequest = {
      model: model,
      content: [
        {
          type: 'text',
          text: `${productName} - ${productFeatures}`
        },
        {
          type: 'image_url',
          image_url: {
            url: selectedImage || ''
          }
        }
      ],
      duration: parseInt(duration),
      camera_fixed: false,
      watermark: true
    };

    try {
      const response = await fetch(`${baseUrl}/contents/generations/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ARK_API_KEY}`
        },
        body: JSON.stringify(videoRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '视频生成请求失败');
      }

      const taskData = await response.json();
      console.log('视频生成任务创建成功:', taskData);

      if (user) {
        const created = await createHistory(user.id, {
          type: 'video',
          category: 'video',
          status: 'pending',
          task_id: taskData.id,
          prompt: `${productName} - ${productFeatures}`,
          input_images: selectedImage ? [selectedImage] : undefined,
        });
        if (created.success && created.id) {
          setCurrentHistoryId(created.id);
        }
      }

      return taskData;
    } catch (err: any) {
      console.error('视频生成失败:', err);
      return null;
    }
  };

  // 存储生成的视频URL
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);

  // 查询任务状态
  const checkTaskStatus = async (taskId: string) => {
    if (!volcengineConfig?.api_key) {
      throw new Error('火山模型API密钥未配置');
    }

    setIsCheckingTaskStatus(true);
      setError(null);

      try {
        const ARK_API_KEY = volcengineConfig.api_key;
        const baseUrl = volcengineConfig.base_url || 'https://ark.cn-beijing.volces.com/api/v3';

        const response = await fetch(`${baseUrl}/contents/generations/tasks/${taskId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ARK_API_KEY}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '查询任务状态失败');
      }

      const taskData = await response.json();
      console.log('任务状态查询成功:', taskData);
      
      setTaskStatus(taskData.status);
      
      // 根据任务状态设置进度
      switch (taskData.status) {
        case 'queued':
          setProgress(20);
          break;
        case 'running':
          // 模拟进度增加
          setProgress(prev => Math.min(prev + 10, 90));
          break;
        case 'succeeded':
          setProgress(100);
          // 任务成功后停止轮询
          stopPollingTaskStatus();
          // 获取视频URL
          if (taskData.content && taskData.content.video_url) {
            setGeneratedVideoUrl(taskData.content.video_url);
          }
          break;
        case 'failed':
        case 'expired':
          setProgress(0);
          // 任务失败或超时时停止轮询
          stopPollingTaskStatus();
          break;
        default:
          break;
      }
      
      // 更新数据库中的任务状态
      if (user) {
        try {
          if (currentHistoryId) {
            await updateHistory(currentHistoryId, {
              status: taskData.status,
              output_url: taskData.content?.video_url,
              task_id: taskId,
            });
          } else {
            const { history } = await getUserHistory(user.id, 'video', 20);
            const matched =
              history.find((item) => item.task_id === taskId) ||
              history.find((item) => item.status === 'pending' || item.status === 'processing') ||
              history[0];
            if (matched?.id) {
              await updateHistory(matched.id, {
                status: taskData.status,
                output_url: taskData.content?.video_url,
                task_id: taskId,
              });
              setCurrentHistoryId(matched.id);
            }
          }
        } catch (updateError) {
          console.error('更新历史记录状态失败:', updateError);
        }
      }

      setIsCheckingTaskStatus(false);
      return taskData;
    } catch (err: any) {
      console.error('查询任务状态失败:', err);
      setError(err.message || '查询任务状态失败');
      setIsCheckingTaskStatus(false);
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />

      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">一键出大片</h1>
            <p className="text-gray-400 text-sm mb-8">
              上传产品主图并填写卖点，系统将自动生成专业视频素材与制作所需步骤，帮助你快速出产品出片广告片。
            </p>

            {/* 错误提示 */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-400"></i>
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* 视频生成进度 */}
            {videoTaskId && (
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">视频生成进度</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-400">任务状态</span>
                      <span className={`text-sm font-medium ${
                        taskStatus === 'succeeded' ? 'text-green-400' :
                        taskStatus === 'failed' || taskStatus === 'expired' ? 'text-red-400' :
                        taskStatus === 'running' ? 'text-blue-400' :
                        'text-yellow-400'
                      }`}>
                        {taskStatus === 'queued' && '排队中'}
                        {taskStatus === 'running' && '生成中'}
                        {taskStatus === 'succeeded' && '生成成功'}
                        {taskStatus === 'failed' && '生成失败'}
                        {taskStatus === 'expired' && '任务超时'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2.5">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-teal-500 h-2.5 rounded-full transition-all duration-300" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-gray-500">0%</span>
                      <span className="text-xs text-gray-500">{progress}%</span>
                      <span className="text-xs text-gray-500">100%</span>
                    </div>
                  </div>
                  {taskStatus === 'succeeded' && (
                    <div className="space-y-4">
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <i className="ri-check-circle-line text-green-400"></i>
                          <p className="text-green-400 text-sm">视频生成成功！</p>
                        </div>
                      </div>
                      {generatedVideoUrl && (
                        <div className="bg-[#0a0a0a] rounded-lg p-4 border border-gray-700">
                          <h4 className="text-sm font-semibold mb-3">生成的视频</h4>
                          <video
                            src={generatedVideoUrl}
                            controls
                            autoPlay
                            muted
                            loop
                            className="w-full rounded-lg"
                          />
                          <div className="mt-4 flex justify-between items-center">
                            <span className="text-xs text-gray-400">点击视频播放</span>
                            <a
                              href={generatedVideoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                            >
                              <i className="ri-download-line"></i>
                              <span>下载视频</span>
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {((taskStatus === 'failed') || (taskStatus === 'expired')) && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <i className="ri-close-circle-line text-red-400"></i>
                        <p className="text-red-400 text-sm">
                          {taskStatus === 'failed' ? '视频生成失败，请重试' : '任务超时，请重试'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 左右分栏：左侧生成产品信息，右侧展示分镜图与输出设置 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* ---------- 左侧：上传产品图 + 第一步 ---------- */}
              <div className="space-y-6">
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-upload-cloud-2-line text-purple-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">上传产品图</h3>
                  </div>
                  <div
                    className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center hover:border-purple-500/50 transition-colors cursor-pointer relative min-h-[200px] flex flex-col items-center justify-center"
                    onDrop={handleImageDrop}
                    onDragOver={handleImageDragOver}
                  >
                    {selectedImage ? (
                      <div className="relative w-full">
                        <img
                          src={selectedImage}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg object-contain"
                        />
                        <button
                          onClick={() => setSelectedImage(null)}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <i className="ri-close-line text-white"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleImageUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                          <i className="ri-upload-cloud-2-line text-purple-400 text-3xl"></i>
                        </div>
                        <p className="text-gray-400 text-sm mb-1">点击上传或拖拽图片至此</p>
                        <p className="text-gray-500 text-xs">支持 PNG / JPG / WEBP 格式, 大小不超过 5MB</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-lightbulb-line text-purple-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">第一步</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                        <i className="ri-lock-line text-gray-400"></i>
                        <span>产品名称</span>
                      </label>
                      <input
                        type="text"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        placeholder="例如: 全息影像投影仪"
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                        <i className="ri-star-line text-orange-400"></i>
                        <span>核心功能/感受</span>
                        <span className="text-xs text-gray-500">最多30个字</span>
                      </label>
                      <input
                        type="text"
                        value={productFeatures}
                        onChange={(e) => setProductFeatures(e.target.value)}
                        placeholder="描述产品的主要卖点、使用场景或带来的感受"
                        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                        maxLength={30}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        描述产品的主要卖点、使用场景或带来的感受, 用于生成分镜关键词 (最多30个字)。
                      </p>
                      <div className="text-xs text-gray-500 mt-0.5 text-right">{productFeatures.length}/30</div>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                        <i className="ri-palette-line text-cyan-400"></i>
                        <span>分镜图风格</span>
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {styles.map((style) => (
                          <button
                            key={style}
                            onClick={() => setSelectedStyle(style)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                              selectedStyle === style
                                ? 'bg-blue-600 text-white'
                                : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                            }`}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={handleInitialize}
                      disabled={!selectedImage || !productName || !productFeatures || isInitializing}
                      className={`w-full px-6 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                        selectedImage && productName && productFeatures && !isInitializing
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20'
                          : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isInitializing ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          <span>初始化中...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-play-circle-line"></i>
                          <span>初始化产品信息</span>
                          <span className="bg-white/20 px-2 py-0.5 rounded text-xs">10 点</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* ---------- 右侧：分镜图展示 + 输出尺寸 + 视频时长 + 生成视频 ---------- */}
              <div className="space-y-6">
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-image-add-line text-purple-400 text-lg"></i>
                      <h3 className="text-lg font-semibold">生成产品视频</h3>
                    </div>
                    {isInitialized && storyboardImages.length > 0 && (
                      <button
                        onClick={handleRegenerateStoryboard}
                        disabled={isGeneratingStoryboard}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                          isGeneratingStoryboard
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : 'bg-[#0a0a0a] border border-gray-700 text-gray-300 hover:border-blue-500'
                        }`}
                      >
                        {isGeneratingStoryboard ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            <span>生成中...</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-refresh-line"></i>
                            <span>重新生成分镜图片</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {storyboardImages.length > 0 ? (
                    <div className="grid grid-cols-3 gap-3 max-h-[340px] overflow-y-auto pr-2 custom-scrollbar">
                      {storyboardImages.map((image) => (
                        <div
                          key={image.id}
                          className="bg-[#0a0a0a] rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all group"
                        >
                          <div className="relative aspect-video bg-[#0a0a0a] overflow-hidden">
                            <img
                              src={image.url}
                              alt={`分镜 ${image.id}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                              {image.id}
                            </div>
                          </div>
                          <div className="p-2">
                            <p className="text-xs text-gray-400 line-clamp-2">{image.prompt}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center min-h-[260px] flex flex-col items-center justify-center">
                      <div className="w-20 h-20 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-image-add-line text-purple-400 text-4xl"></i>
                      </div>
                      <p className="text-gray-400 mb-2">生成后在此展示分镜图</p>
                      <p className="text-gray-500 text-xs">请先完成左侧步骤并初始化产品信息</p>
                    </div>
                  )}
                </div>

                {/* 输出尺寸、视频时长、生成按钮（进入页面即显示，生成视频需先完成左侧初始化） */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-aspect-ratio-line text-cyan-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">输出尺寸</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {ratios.map((ratio) => (
                      <button
                        key={ratio.value}
                        onClick={() => setSelectedRatio(ratio.value)}
                        className={`py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedRatio === ratio.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        {ratio.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-time-line text-green-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">视频时长</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {durations.map((duration) => (
                      <button
                        key={duration}
                        onClick={() => setSelectedDuration(duration)}
                        className={`py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedDuration === duration
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        {duration}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleGenerateVideo}
                  disabled={isGeneratingVideo || !isInitialized || storyboardImages.length === 0}
                  className={`w-full py-4 rounded-xl text-white font-semibold transition-all shadow-lg cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    isGeneratingVideo || !isInitialized || storyboardImages.length === 0
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-blue-500/20 hover:shadow-blue-500/40'
                  }`}
                >
                  {isGeneratingVideo ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      <span>提交中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-play-circle-line text-xl"></i>
                      <span>生成产品大片视频（{selectedDuration}）</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                        {selectedDuration === '10秒' ? '15' : '10'} 点
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* 温馨提示 - 底部全宽 */}
            <div className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-2xl p-6 border border-blue-500/20">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-blue-400"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-blue-400 mb-2">温馨提示</h4>
                  <ul className="text-xs text-gray-400 space-y-1">
                    <li>• 上传产品主图并填写名称、卖点后点击「初始化产品信息」,系统会扣除初始化点数并弹出确认框,按提示继续生成分镜图。</li>
                    <li>• 首版分镜图会自动生成并展示在右侧;如需调优可点击「重新生成分镜图片」,每次会消耗分镜图点数。如更换主图或文案,请重新初始化。</li>
                    <li>• 确认分镜图无误后,选择输出尺寸与视频时长,再点击「生成产品大片视频」提交,提交时会扣除对应视频点数。</li>
                    <li>• 视频生成约需3-5分钟,请前往「素材空间」或「历史记录」查看进度并下载成片。</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ---------- Confirm Modal ---------- */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full border border-gray-800 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-information-line text-white text-3xl"></i>
              </div>

              <h2 className="text-2xl font-bold mb-3">确认初始化</h2>

              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                初始化将消耗 10 点，系统将根据您上传的产品图和填写的信息自动生成分镜图。确认继续？
              </p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-6 py-3 bg-[#0a0a0a] border border-gray-700 rounded-lg text-sm font-medium hover:border-gray-600 transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirmInitialize}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Success Modal ---------- */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full border border-gray-800 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>

              <h2 className="text-2xl font-bold mb-3">提交成功</h2>

              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                视频生成任务已提交成功，约 3-5 分钟后可在「素材空间」或「历史记录」查看进度并下载成片。
              </p>

              {videoTaskId && (
                <div className="w-full bg-[#0a0a0a] rounded-lg p-4 mb-4 border border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-400">任务ID</span>
                    <span className="text-xs text-blue-400 font-mono">{videoTaskId.slice(0, 12)}...</span>
                  </div>
                  {taskStatus && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">当前状态</span>
                      <span className={`text-xs font-medium ${
                        taskStatus === 'completed' ? 'text-green-400' :
                        taskStatus === 'failed' ? 'text-red-400' :
                        'text-yellow-400'
                      }`}>
                        {taskStatus === 'completed' ? '已完成' :
                         taskStatus === 'failed' ? '失败' :
                         taskStatus === 'pending' ? '处理中' :
                         taskStatus === 'running' ? '生成中' :
                         taskStatus}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 w-full mb-3">
                <button
                  onClick={handleCheckTaskStatus}
                  disabled={isCheckingTaskStatus || !videoTaskId}
                  className={`flex-1 px-6 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    isCheckingTaskStatus || !videoTaskId
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'bg-[#0a0a0a] border border-gray-700 text-gray-300 hover:border-blue-500'
                  }`}
                >
                  {isCheckingTaskStatus ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>查询中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line"></i>
                      <span>查询状态</span>
                    </>
                  )}
                </button>
              </div>

              <button
                onClick={handleSuccessConfirm}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom scrollbar style */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
}
